import { useEffect, useState } from "react"
import { MdFileDownload } from "react-icons/md"
import { isAuthenticated, getUserSession, formatNumber, clearSession } from "../../utils/utils"
import { ApiClient } from "../../utils/apiclient"
import { useNavigate } from "react-router-dom"
import { ReportService } from "../../utils/services/report.service"
import ReactApexChart from "react-apexcharts";
import { DateTime } from "luxon"
import VehicleFuelTable from "./vehicle_fuel_table"
import DateSelectorDialog from "./date_picker_dialog"
import exportFromJson from 'export-from-json'
import Spinner from "../../components/spinner"
import Tooltip from "@material-ui/core/Tooltip"
import CalendarView from "@material-ui/icons/CalendarTodaySharp"
import { clear } from "console"

const FuelReport = () => {
    const session = getUserSession();
    const apiClient = new ApiClient(session?.accessToken)
    const reportService = new ReportService(apiClient)
    const navigate = useNavigate();

    const [startDate, setStartDate] = useState<number>(DateTime.fromMillis(Date.now()).minus({ month: 1 }).toMillis())
    const [endDate, setEndDate] = useState<number>(Date.now())
    const [reports, setReports] = useState<any>();
    const [seriesFuel, setSeriesFuel] = useState<any[]>([])
    const [seriesCost, setSeriesCost] = useState<any[]>([])
    const [vehicles, setVehicles] = useState<any[]>([])
    const [optionsFuelQuantity, setOptionsFuelQuantity] = useState<any>()
    const [optionsCost, setOptionsCost] = useState<any>()
    const [open, setOpen] = useState<boolean>(false)
    const [loading, setLoading] = useState<boolean>(false)

    const getFuelReports = async (startDate: number, endDate: number) => {
        setLoading(true)
        const reports = await reportService.getFuelReport(startDate, endDate);
        if (reports.status == 401) {
            clearSession();
            navigate('/login', { state: { targetPath: window.location.pathname } })
        }
        if (reports.status == 200) {
            const vehicleList = reports.data?.consumptions.map((v: any) => v.vehicle);
            const optionsCostOfFuelByVehicle = {
                chart: {
                    id: 'fuel-report',
                    toolbar: { show: true }

                },
                title: {
                    text: 'Cost of Fuel by Vehicle'
                },
                stroke: {
                    show: true,
                    curve: 'smooth',
                    lineCap: 'butt',
                    colors: undefined,
                    width: 2,
                    dashArray: 0,
                },
                tooltip: {
                    y: {
                        formatter: function (val: any) {
                            return val + "K"
                        }
                    },
                    position: 'top'
                },
                xaxis: {
                    categories: vehicleList
                }
            }
            const optionsFuelQuantity = {
                chart: {
                    id: 'fuel-report',
                    toolbar: { show: true }

                },
                title: {
                    text: 'Fuel Quantity by Vehicle'
                },
                stroke: {
                    show: true,
                    curve: 'smooth',
                    lineCap: 'butt',
                    colors: undefined,
                    width: 2,
                    dashArray: 0,
                },
                tooltip: {
                    y: {
                        formatter: function (val: number, opts: { seriesIndex: number; dataPointIndex: number; w: any }) {
                            // Example: customize per series
                            if (opts.seriesIndex === 0) return `${val} Lt`;
                            if (opts.seriesIndex === 1) return `${val} Km (x10)`;
                            return `${val}`;
                        }
                    },
                    position: 'top'
                },
                xaxis: {
                    categories: vehicleList
                }
            }
            const series = [
                {
                    name: "Fuel",
                    data: reports.data?.consumptions?.map((d: any) => d.fuel)
                },
                {
                    name: "Distance",
                    data: reports.data?.consumptions?.map((d: any) => d.distance/10)
                },
            ]

            setSeriesFuel(series)
            setSeriesCost([{
                name: "Cost",
                data: reports.data.consumptions.map((d: any) => (d.cost).toFixed(1))
            }])
            // setSeriesConsumption(seriesAverageConsumption);
            // setSeriesOptionsConsumption(optionsConsumption)
            setVehicles(vehicleList)
            setOptionsFuelQuantity(optionsFuelQuantity)
            setOptionsCost(optionsCostOfFuelByVehicle)
            setReports(reports.data)

        }
        setLoading(false)
    }
    const handleCalendarClick = () => {
        setOpen(true)
    }
    useEffect(() => {
        if (!isAuthenticated()) {
            navigate('/login', { state: { targetPath: window.location.pathname } })
        }
        else {
            getFuelReports(startDate, endDate);
        }

    }, [startDate, endDate])
    const handleSubmit = (formData: any) => {
        console.log("🚀 ~ handleSubmit ~ formData:", formData)
        setStartDate(Date.parse(formData.startDate))
        setEndDate(Date.parse(formData.endDate))
    }
    const downloadFile = () => {
        const data = reports?.consumptions
        const fileName = 'fuel-consumption';
        const exportType = 'csv';
        exportFromJson({ data, fileName, exportType })
    }
    return (<>
        <div className="w-full">
            {isAuthenticated() ?
                <div className="w-full flex flex-col justify-start items-start mx-auto border p-4">
                    {open ? <DateSelectorDialog state={open} onClose={() => { setOpen(false) }} onSubmit={handleSubmit} /> : null}
                    {loading ? <Spinner className="spinner-md mt-6" /> : <><div className="flex justify-between items-center space-x-4"><p className="text-md">{`Report for ${DateTime.fromMillis(startDate).toLocaleString(DateTime.DATETIME_MED)} - ${DateTime.fromMillis(endDate).toLocaleString(DateTime.DATETIME_MED)}`}</p>
                        <Tooltip title="Select date">
                            <CalendarView className="text-accent text-1xl cursor-pointer" onClick={handleCalendarClick} />
                        </Tooltip>

                    </div>
                        {reports ? <><div className="w-full flex justify-start space-x-4 items-start mx-auto text-gray-500 py-4 text-sm border-b border-b-gray-400">
                            <span>Total Distance: <span className="font-bold">{formatNumber(reports.totalDistance)}Km</span></span>
                            <span>Total Consumption: <span className="font-bold">{formatNumber(reports.totalLiters)}L</span></span>
                            <span>Avg. Consumption per Vehicle:  <span className="font-bold">{formatNumber(reports.averageLiterPerVehicle)}L</span></span>
                            <span>Avg. Distance per Vehicle:  <span className="font-bold">{formatNumber(reports.averageTripPerVehicle)}Km</span></span>
                            <span>Average Consumption:  <span className="font-bold">{`${reports?.averageConsumption}Km/L`}</span></span>
                        </div>
                            <div className="w-full flex justify-start items-start space-x-2 mt-4">
                                <div className="w-6/12">
                                    {optionsFuelQuantity && seriesFuel ? <ReactApexChart type="area" options={optionsFuelQuantity} series={seriesFuel} /> : null}
                                </div>
                                <div className="w-6/12">
                                    {optionsCost && seriesCost ? <ReactApexChart type="line" options={optionsCost} series={seriesCost} /> : null}
                                </div>
                            </div>
                            <div className="w-full mt-4">
                                <div className="w-full flex justify-between items-center">
                                    <p>Fuel Reports by Vehicle</p>
                                    <MdFileDownload className="text-2xl text-accent cursor-pointer" onClick={downloadFile} />
                                </div>
                                <VehicleFuelTable data={reports?.consumptions} />
                            </div>
                        </> : <p className="text-center my-6">No Data</p>}
                    </>}</div>

                : <span>You need to login</span>}
        </div >
    </>)
}
export default FuelReport;